import { toast } from 'react-toastify';
import { listingNavigationButtonSx } from './navigationStyles';
import { requestAdminNavigation } from 'src/navigation/adminNavigationGuard';
import { QuoteNavigationActionsContext } from 'src/content/applications/quote/QuoteEditorHeaderActions';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import { ReactNode, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Link,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Portal,
  Typography
} from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { useAdminWorkspace } from 'src/workspaces/AdminWorkspaceProvider';

type Publication = {
  eventId: number;
  companyId: number;
  published: boolean;
  quoteStatus?: string;
  quoteApproved?: boolean;
  everPublished: boolean;
  unpublishedAt: string | null;
  state: string;
  publishedAt: string | null;
  publishedBy: number | null;
  deferredAt: string | null;
  readyToPublish: boolean;
  blockingReasons: string[];
  advisoryReasons: string[];
  workerRatesConfigured?: boolean;
};
const eventName = 'avwf:quote-approved';
export function usePublication(eventId: number | null) {
  const workspace = useAdminWorkspace();
  const company =
    workspace.kind === 'company' && workspace.status === 'ready'
      ? workspace.workspace
      : null;
  const companyId = company?.company.id;
  const allowed = Boolean(
    company &&
      (company.accessLevels ?? [company.accessLevel]).some(
        (level) => level === 'COMPANY_ADMIN' || level === 'OPERATIONS_MANAGER'
      )
  );
  const path = `/companies/${companyId}/events/${eventId}/publication`;
  const queryClient = useQueryClient();
  const query = useQuery<Publication, Error>({
    queryKey: ['job-publication', companyId, eventId],
    enabled: Boolean(companyId && eventId),
    queryFn: async () => (await axios.get(path)).data.data
  });
  const keys = useRef<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const act = async (action: 'publish' | 'defer' | 'unpublish') => {
    if (!allowed || !eventId || !companyId || busy) return false;
    const attempt = `${companyId}:${eventId}:${action}`;
    keys.current[attempt] ??= `publication:${crypto.randomUUID()}`;
    setBusy(true);
    setError('');
    try {
      const result = await axios.post(
        path,
        { action },
        { headers: { 'Idempotency-Key': keys.current[attempt] } }
      );
      queryClient.setQueryData(
        ['job-publication', companyId, eventId],
        result.data.data
      );
      await queryClient.invalidateQueries({ queryKey: ['job-publication'] });
      await queryClient.invalidateQueries({
        queryKey: ['contract-lifecycle', companyId, eventId]
      });
      delete keys.current[attempt];
      return true;
    } catch (failure) {
      setError(
        (failure as { response?: { data?: { message?: string } } }).response
          ?.data?.message ||
          'Unable to update job listings. Retry the same action.'
      );
      return false;
    } finally {
      setBusy(false);
    }
  };
  return { ...query, allowed, visible: Boolean(companyId), busy, error, act };
}

export function JobPublicationStatus({
  eventId,
  onViewBids,
  actionButtonSx,
  embedded = false,
  footer = false,
  feedbackContainer,
  actionContainer
}: {
  eventId: number;
  embedded?: boolean;
  footer?: boolean;
  feedbackContainer?: HTMLElement | null;
  actionContainer?: () => HTMLElement | null;
  onViewBids?: () => void;
  actionButtonSx?: SxProps<Theme>;
}) {
  const publication = usePublication(eventId);
  const navigationContainer = useContext(QuoteNavigationActionsContext);
  const [confirm, setConfirm] = useState(false);
  const renderFeedback = (content: ReactNode) => {
    if (footer && !feedbackContainer) return null;
    return <Portal container={feedbackContainer} disablePortal={!footer}>{content}</Portal>;
  };
  if (!publication.visible) return onViewBids ? (
    <Box sx={{ m: 2, display: 'flex', justifyContent: 'flex-end' }}>
      <Button variant="contained" onClick={onViewBids}>View All Bids</Button>
    </Box>
  ) : null;
  if (publication.isLoading)
    return (
      <Box sx={{ p: 2 }}>
        <CircularProgress size={20} aria-label="Loading publication status" />
      </Box>
    );
  if (publication.isError)
    return renderFeedback(
      <Alert
        severity="error"
        action={<Button onClick={() => publication.refetch()}>Retry</Button>}
      >
        Unable to load job publication status.
      </Alert>
    );
  const state = publication.data;
  if (!state) return null;
  const canceled = state.state === 'CANCELED';
  const approved = state.quoteApproved ?? !['AWAITING_APPROVAL'].includes(state.state);
  const quoteStatusLabel = state.quoteStatus
    ? state.quoteStatus.charAt(0).toUpperCase() + state.quoteStatus.slice(1)
    : 'Draft';
  const wasPublished = state.everPublished || Boolean(state.publishedAt);
  const action = state.published ? 'unpublish' : 'publish';
  const publicationBlocked =
    !state.published &&
    (state.readyToPublish === false || (state.blockingReasons?.length ?? 0) > 0);
  const unconfiguredPublish = !state.published && state.workerRatesConfigured === false;
  return (
    <>
      {!approved && !canceled && renderFeedback(
        <Alert severity="warning" sx={{ m: embedded ? 0 : 2, mb: 1 }}>
          <strong>Quote Not Approved</strong> — Publishing opens worker bidding; it does not approve the quote or change deposits.{' '}
          <Link href={`/dashboards/jobTracker/positions/${eventId}?tab=quote`}
            onClick={(event) => {
              const path = `/dashboards/jobTracker/positions/${eventId}?tab=quote`;
              if (!requestAdminNavigation(() => window.location.assign(path))) event.preventDefault();
            }}>Review quote</Link>
        </Alert>
      )}
      <Stack
        direction={embedded ? 'column' : { xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{
          m: embedded ? 0 : 2,
          p: embedded ? 0 : onViewBids ? 2 : 2.5,
          bgcolor: 'background.paper',
          border: embedded ? 'none' : '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          alignItems: embedded ? 'flex-end' : { sm: 'center' },
          ...(embedded ? { textAlign: 'right' } : {}),
          ...(onViewBids ? {
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 260px minmax(0, 1fr)' },
            gap: 2,
            '& > :not(style) ~ :not(style)': { margin: 0 }
          } : {}),
          ...(footer ? {
            m: 0, px: { xs: 2, sm: 3, md: 6 }, py: 1.5, pb: 'max(12px, env(safe-area-inset-bottom))', display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr) auto', flexDirection: 'row',
            flexWrap: 'nowrap', alignItems: 'center', gap: 2,
            border: 0, borderRadius: 0, bgcolor: 'transparent',
            whiteSpace: 'nowrap', minWidth: 0,
            '& button': { whiteSpace: 'nowrap', flexShrink: 0, height: 36, minHeight: 36, py: 0 },
            '& > :not(style) ~ :not(style)': { margin: 0 }
          } : {})
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: footer ? 'contents' : onViewBids ? 'flex' : 'block',
            ...(onViewBids ? { gridColumn: { lg: 1 }, gridRow: { lg: 1 }, textAlign: 'left' } : {}),
            alignItems: 'center',
            gap: 1.25,
            flexWrap: footer || (onViewBids && state.published) ? 'nowrap' : 'wrap'
          }}
        >
          <Typography variant="h5" sx={onViewBids ? { textAlign: 'left', whiteSpace: 'nowrap' } : undefined}>
            Job Listings{' '}
            <Chip
              size="small"
              icon={state.published ? <CheckCircleRounded /> : undefined}
              label={
                state.published
                  ? 'Published'
                  : state.state === 'CANCELED'
                  ? 'Canceled'
                  : wasPublished
                  ? 'Unpublished'
                  : 'Not published'
              }
              sx={{ ml: 1, ...(state.published ? { width: 150, height: 34, borderRadius: '8px', border: '1px solid #2e7d32', backgroundColor: '#edf7ed', color: '#1b5e20', fontWeight: 800, '.MuiChip-icon': { color: '#2e7d32', ml: 1 } } : {}) }}
            />
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            title={onViewBids && state.publishedAt ? `Published ${new Date(state.publishedAt).toLocaleString()}` : undefined}
            sx={{ display: footer ? 'none' : undefined, mt: onViewBids ? 0 : 1, ...(onViewBids ? { textAlign: 'left', whiteSpace: state.published ? 'nowrap' : 'normal' } : {}) }}
          >
            {state.published
              ? `Live for eligible workers${
                  !onViewBids && state.publishedAt
                    ? ` · Published ${new Date(
                        state.publishedAt
                      ).toLocaleString()}`
                    : ''
                }`
              : state.state === 'CANCELED'
              ? 'This event is canceled.'
              : wasPublished
              ? 'Hidden from workers. Existing bids remain available for review; publish again to assign bids.'
              : publicationBlocked
              ? state.blockingReasons?.join(' ') || 'Complete the required publication steps before publishing.'
              : !approved
              ? 'Publish explicitly to open these listings to eligible workers. Client approval and deposits remain unchanged.'
              : 'Workers cannot see these listings or place any bids until jobs are published.'}
          </Typography>
          {(state.advisoryReasons?.length ?? 0) > 0 && renderFeedback(
            <Alert severity="warning" sx={{ mt: 1 }}>
              {state.advisoryReasons.join(' ')}
            </Alert>
          )}
        </Box>
        <Portal container={actionContainer} disablePortal={!actionContainer}>
        <Box sx={{ display: onViewBids ? 'contents' : 'flex', gap: 2, flexWrap: 'wrap', ...(onViewBids ? { gridColumn: 1, gridRow: 1, justifySelf: 'start' } : {}) }}>
        {onViewBids && (
          <Button
            variant="contained"
            sx={{ ...listingNavigationButtonSx(state.published), gridColumn: footer ? 3 : { lg: 2 }, gridRow: footer ? 1 : { lg: 1 }, justifySelf: footer ? 'end' : 'center' }}
            onClick={onViewBids}
          >
            View All Bids
          </Button>
        )}
        {!canceled && publication.allowed && (
          <Portal container={navigationContainer} disablePortal={!footer || !navigationContainer}>
          <Button variant={state.published || unconfiguredPublish ? 'outlined' : 'contained'} sx={state.published ? { gridColumn: { lg: 3 }, gridRow: { lg: 1 }, justifySelf: { lg: 'end' }, backgroundColor: 'transparent', color: '#b42318', borderColor: '#b42318', boxShadow: 'none', '&:hover': { backgroundColor: 'transparent', color: '#912018', borderColor: '#912018', boxShadow: 'none' } } : unconfiguredPublish ? {
            ...(typeof actionButtonSx === 'object' && !Array.isArray(actionButtonSx) ? actionButtonSx : {}),
            borderColor: '#111', color: '#111', backgroundColor: 'transparent', boxShadow: 'none',
            '&:hover': { borderColor: '#111', backgroundColor: '#f5f5f5', boxShadow: 'none' }
          } : actionButtonSx} onClick={() => {
            if (!state.published) {
              let draft: any = null;
              try { draft = JSON.parse(sessionStorage.getItem(`avwf:booking-worker-rates:${eventId}`) || 'null'); } catch { /* Stored draft may be unavailable. */ }
              if (draft?.mode === 'required' && draft.shifts?.some(row => !Number.isFinite(Number(row.requiredRate)) || Number(row.requiredRate) <= 0)) {
                toast.error('Every shift needs a Required Rate greater than $0 or Open Rate before publishing.'); return;
              }
            }
            if (publicationBlocked) { toast.error(state.blockingReasons?.join(' ') || 'Complete publication setup before publishing.'); return; }
            setConfirm(true);
          }}>
            {state.published
              ? 'Unpublish Job Listings'
              : 'Publish Job Listings'}
          </Button>
          </Portal>
        )}
        </Box>
        </Portal>
      </Stack>
      <Dialog
        open={confirm}
        onClose={() => !publication.busy && setConfirm(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {state.published
            ? 'Unpublish job listings?'
            : !approved
            ? 'Publish Before Client Approval?'
            : 'Publish job listings?'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {state.published
              ? 'These listings will be hidden from workers. Existing bids and assignments are preserved. Bids can be reviewed but cannot be assigned until you publish again.'
              : !approved
              ? `This opens these job listings to eligible workers. The client quote stays ${quoteStatusLabel}. Client approval and deposit status do not change.`
              : wasPublished
              ? 'These listings will become visible to eligible workers again. Existing bids can be assigned after publication. Quote, invoice and worker-pay amounts stay unchanged.'
              : 'These listings will become visible to eligible workers. Workers can then place bids, and eligible bids can be assigned. Quote, invoice and worker-pay amounts stay unchanged.'}
          </Typography>
          {publication.error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {publication.error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={publication.busy} onClick={() => setConfirm(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={publication.busy}
            onClick={async () => {
              if (await publication.act(action)) setConfirm(false);
            }}
          >
            {state.published ? 'Unpublish now' : !approved ? 'Publish Job Listings' : 'Publish now'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export function JobPublicationApprovalPrompt() {
  const [bookingsEventId, setBookingsEventId] = useState<number | null>(null);
  const [eventId, setEventId] = useState<number | null>(null);
  const queryClient = useQueryClient();
  const publication = usePublication(eventId);
  useEffect(() => {
    const listener = (event: Event) => {
      const id = Number((event as CustomEvent).detail?.eventId);
      if (Number.isSafeInteger(id) && id > 0) {
        void queryClient.invalidateQueries({ queryKey: ['job-publication'] });
        // Retained Event tabs use both route-string and numeric IDs. Refresh
        // only this approved event, leaving other editable drafts untouched.
        void queryClient.invalidateQueries({
          predicate: ({ queryKey }) =>
            (queryKey[0] === 'qoute' || queryKey[0] === 'jobs') &&
            Number(queryKey[1]) === id
        });
        setEventId(id);
      }
    };
    window.addEventListener(eventName, listener);
    return () => window.removeEventListener(eventName, listener);
  }, [queryClient]);
  const finish = async (action: 'publish' | 'defer' | 'unpublish') => {
    if (await publication.act(action)) { setBookingsEventId(eventId); setEventId(null); }
  };
  return (
    <>
    <Dialog
      open={Boolean(eventId && publication.allowed)}
      onClose={() => {
        if (publication.data?.published) setEventId(null);
        else if (
          !publication.busy &&
          !publication.isLoading &&
          !publication.isError
        )
          void finish('defer');
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Quote approved</DialogTitle>
      <DialogContent>
        <Typography variant="h5" gutterBottom>
          Make these job listings live now?
        </Typography>
        <Typography>
          If you defer, the listings stay hidden from workers. You can publish
          them later from the Event Dashboard or Bookings.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Approval and deposit receipt do not publish listings.
        </Typography>
        {publication.isLoading && (
          <CircularProgress size={20} aria-label="Loading publication status" />
        )}
        {(publication.error || publication.isError) && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {publication.error ||
              'Unable to load publication status. Listings remain held.'}
            <Button onClick={() => publication.refetch()}>Retry</Button>
          </Alert>
        )}
        {publication.data?.published && (
          <Alert severity="info" sx={{ mt: 2 }}>
            These listings are already published.
          </Alert>
        )}
        {!publication.data?.published &&
          (publication.data?.blockingReasons?.length ?? 0) > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {publication.data!.blockingReasons.join(' ')}
            </Alert>
          )}
        {(publication.data?.advisoryReasons?.length ?? 0) > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {publication.data!.advisoryReasons.join(' ')}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        {publication.isError && (
          <Button onClick={() => setEventId(null)}>Close</Button>
        )}
        <Button
          disabled={
            publication.busy || publication.isLoading || publication.isError
          }
          onClick={() =>
            publication.data?.published
              ? setEventId(null)
              : void finish('defer')
          }
        >
          {publication.data?.published ? 'Close' : 'Defer'}
        </Button>
        {!publication.data?.published && publication.data?.readyToPublish === false && (
          <Button onClick={() => {
            const search = new URLSearchParams(window.location.search);
            search.set('tab', 'bookings');
            window.location.assign(`/dashboards/jobTracker/positions/${eventId}?${search}`);
          }}>Set Up Rates in Bookings</Button>
        )}
        {!publication.data?.published && (
          <Button
            variant="contained"
            disabled={
              publication.busy || publication.isLoading || publication.isError
              || publication.data?.readyToPublish === false
            }
            onClick={() => void finish('publish')}
          >
            Publish now
          </Button>
        )}
      </DialogActions>
    </Dialog>
    <Dialog open={Boolean(bookingsEventId)} onClose={() => setBookingsEventId(null)} maxWidth="xs" fullWidth>
      <DialogTitle>Continue To Bookings</DialogTitle>
      <DialogContent>The quote is approved. Continue to Bookings to manage this event’s staffing.</DialogContent>
      <DialogActions>
        <Button onClick={() => setBookingsEventId(null)}>Stay On Quote</Button>
        <Button variant="contained" onClick={() => {
          const search = new URLSearchParams(window.location.search);
          search.set('tab', 'bookings');
          window.location.assign(`/dashboards/jobTracker/positions/${bookingsEventId}?${search}`);
        }}>Go To Bookings</Button>
      </DialogActions>
    </Dialog>
    </>
  );
}
